import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Clock, Users, ChevronDown, ChevronUp, X, Armchair, Baby, Check } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { differenceInMinutes } from "date-fns";
import { cn } from "@/lib/utils";

interface WaitlistReservation {
  id: number;
  supabaseId: string;
  name: string;
  persons: number;
  numberOfChildren: number;
  createdAt: Date;
  phone: string;
  notes?: string;
  tableName?: string;
  source?: string;
  hasStroller?: boolean;
  time?: string;
}

interface WaitlistBannerProps {
  waitlistReservations: WaitlistReservation[];
  onAssignTable: (reservationId: number) => void;
  onCancel: (reservationId: number) => void;
  onEdit?: (reservationId: number) => void;
  onMarkArrived?: (reservationId: number) => void;
}

export function WaitlistBanner({ waitlistReservations, onAssignTable, onCancel, onEdit, onMarkArrived }: WaitlistBannerProps) {
  const [open, setOpen] = useState(true);
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  if (waitlistReservations.length === 0) return null;

  const cancellingReservation = cancellingId != null
    ? waitlistReservations.find(r => r.id === cancellingId)
    : null;

  return (
    <>
      <Collapsible open={open} onOpenChange={setOpen}>
        <Card className="mb-4 border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20">
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full p-3 cursor-pointer group">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="font-semibold text-sm text-amber-800 dark:text-amber-200">
                  {waitlistReservations.length} en attente
                </span>
              </div>
              {open ? (
                <ChevronUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-3 pb-3 space-y-2">
              {waitlistReservations.map((r) => {
                const waitMinutes = differenceInMinutes(new Date(), r.createdAt);
                const waitText = waitMinutes < 1 ? "à l'instant" : `depuis ${waitMinutes} min`;

                return (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-2 p-2.5 rounded-md bg-background border border-border"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn("font-medium text-sm truncate max-w-[120px] sm:max-w-none", onEdit && "cursor-pointer hover:underline")}
                          onClick={(e) => { if (onEdit) { e.stopPropagation(); onEdit(r.id); } }}
                        >{r.name}</span>
                        {r.source === 'walk_in' && <Badge variant="warning" className="text-[10px] px-1.5 py-0">Walk-in</Badge>}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <Users className="w-3 h-3" />
                        <span>{r.persons} pers.</span>
                        {r.numberOfChildren > 0 && (
                          <span className="flex items-center gap-0.5">
                            <Baby className="w-3 h-3" />
                            +{r.numberOfChildren}
                          </span>
                        )}
                        {r.hasStroller && (
                          <StrollerIcon size={12} className="text-muted-foreground" />
                        )}
                        {r.time && (
                          <>
                            <span className="mx-0.5">·</span>
                            <Clock className="w-3 h-3" />
                            <span>{r.time}</span>
                          </>
                        )}
                        <span className="mx-0.5">·</span>
                        <span>{waitText}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {r.tableName ? (
                        <>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0 max-w-[80px]">
                            <Armchair className="w-3 h-3 mr-0.5 shrink-0" />
                            <span className="truncate">{r.tableName}</span>
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            title="Marquer arrivé"
                            onClick={() => onMarkArrived?.(r.id)}
                          >
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="default"
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => onAssignTable(r.id)}
                        >
                          <Armchair className="w-3.5 h-3.5 sm:mr-1" />
                          <span className="hidden sm:inline">Assigner</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        title="Annuler"
                        onClick={() => setCancellingId(r.id)}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <AlertDialog open={cancellingId != null} onOpenChange={(v) => { if (!v) setCancellingId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer de la liste d'attente ?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancellingReservation
                ? `${cancellingReservation.name} (${cancellingReservation.persons} pers.) sera retiré de la liste d'attente et la réservation sera annulée.`
                : "Cette réservation sera annulée."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Non, garder</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (cancellingId != null) {
                  onCancel(cancellingId);
                }
                setCancellingId(null);
              }}
            >
              Oui, retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
